import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import {
  getNextBillingDateForSchedule,
  getRecurrenceDateForPhases
} from '@sequencehq/utils'
import { add, isAfter, isBefore, sub } from '@sequencehq/utils/dates'

const getEndOfBillingPeriod = (args: {
  endOfSchedule?: Date
  nextBillingDate: Date | null
}) => {
  /**
   * When the schedule already ends inside the current billing period there is no
   * new end date to move it to, so we report that instead of an end date. Handing
   * back the existing end date would ask the update to truncate a phase which
   * already ends there, and that fails.
   */
  const scheduleAlreadyEndsInCurrentPeriod = Boolean(
    args.endOfSchedule &&
      args.nextBillingDate &&
      isBefore(args.endOfSchedule, args.nextBillingDate)
  )

  if (scheduleAlreadyEndsInCurrentPeriod || !args.nextBillingDate) {
    return { endOfBillingPeriod: undefined, scheduleAlreadyEndsInCurrentPeriod }
  }

  return {
    endOfBillingPeriod: sub(args.nextBillingDate, { days: 1 }),
    scheduleAlreadyEndsInCurrentPeriod
  }
}

export const useEndOfCurrentBillingPeriod = (props: {
  billingScheduleId: string
}) => {
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId: props.billingScheduleId,
    select: data => {
      const endDate = data.billingSchedule.endDate

      const today = new Date()
      const phaseRecurrenceDates = getRecurrenceDateForPhases({
        recurrenceDayOfMonth: data.billingSchedule.recurrenceDayOfMonth,
        phases: data.billingSchedule.phases
      })

      const currentPhaseIndex = data.billingSchedule.phases.findIndex(phase => {
        return (
          isAfter(today, phase.startDate) &&
          (!phase.endDate || isBefore(today, phase.endDate))
        )
      })

      const nextBillingDate = getNextBillingDateForSchedule({
        recurrenceDayOfMonth:
          phaseRecurrenceDates[currentPhaseIndex]?.recurrenceDayOfMonth,
        startDate: today,
        endDate: endDate ?? add(new Date(), { years: 1 }),
        prices: data.prices
      })

      return getEndOfBillingPeriod({
        endOfSchedule: endDate,
        nextBillingDate
      })
    }
  })

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    endOfBillingPeriod: billingScheduleAggregateQuery.data?.endOfBillingPeriod,
    scheduleAlreadyEndsInCurrentPeriod:
      billingScheduleAggregateQuery.data?.scheduleAlreadyEndsInCurrentPeriod
  }
}
